/**
 * `resumes create` / `resumes update` 的聚合編排核心（US-1 核心，design doc §3.4／plan Task 14）。
 * 把一份聚合 JSON 拆成 spec B 對應的多個分節寫入請求，依 `SECTION_WRITE_PLAN`
 *（personal-types.ts）派工，逐節記錄成敗、**不回滾**——單節失敗不影響其他節繼續寫入，已成功
 * 寫入的節與（create 情境下）已建的殼都保留，讓使用者用 `update --section <失敗節>` 補寫，
 * 不必整份重來。
 *
 * create 專屬的「建殼」前置步驟例外：失敗代表根本沒有 enc_id 可續寫任何節，屬全有全無的
 * 中止點，直接 throw（不落入分節失敗的 report 陣列）。rename（②b）與其後的分節寫入則一律
 * 走「失敗記報告、繼續下一步」的一般路徑。
 *
 * 本地 `validateAggregate` 前置驗證是呼叫端（create.ts 命令層）的責任，這支檔案不重複驗證，
 * 維持「本地驗證＝快速失敗、編排＝發請求」的分工（update 不做本地整份驗證，見 updateAggregate
 * 註解）。
 */

import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { personalPost, personalPut, type PersonalRequestOptions } from './personal-client';
import { CliError, ExitCode, ServerClientHttpError } from './errors';
import { PERSONAL_RESUMES_BASE, SECTION_WRITE_PLAN, type UpdateResumeNameDto } from '@wport/core';
import { SECTIONS, getSection, type AggregateSectionKey } from '@wport/core';

export interface SectionReport {
	section: string;
	ok: boolean;
	error?: string;
}

export interface OrchestrationResult {
	enc_id: string | null;
	reports: SectionReport[];
	allOk: boolean;
}

type WriteMode = 'create' | 'update';

/** 寫入端點(POST/PUT/PATCH/DELETE 全部)必帶 Idempotency-Key(design doc §5.1)；每次呼叫各自一把。 */
function freshIdempotencyKey(): { idempotencyKey: string } {
	return { idempotencyKey: randomUUID() };
}

/** CliError 與 core 錯誤（ServerClientHttpError／NetworkError，即 core 的 WportHttpError／WportNetworkError alias）皆已是可讀訊息；其餘走 String() 兜底。 */
function describeError(err: unknown): string {
	return err instanceof Error ? err.message : String(err);
}

/** 錯誤 body 的機器可判讀 `error_code`（design doc §5.1：CLI 判斷 key `error_code` 優先於 `statusCode`）。 */
function extractErrorCode(body: unknown): string | null {
	if (body && typeof body === 'object' && typeof (body as Record<string, unknown>).error_code === 'string') {
		return (body as Record<string, unknown>).error_code as string;
	}
	return null;
}

/**
 * 建履歷殼（R3：`POST /api/v1/personal/resumes`，空 body，D8；photo_url 唯讀本就不入殼）。
 * 與其餘寫入步驟不同：失敗代表整個 create 無以為繼（沒有 enc_id 可續寫任何節），直接中止並
 * 依 `error_code` 給引導訊息（BR-002 `profile_incomplete` / BR-010 `resume_limit_reached`），
 * 不落入「記 report 繼續」的一般分節失敗路徑；其他 4xx/5xx 原樣上拋（已經是可讀的 core 錯誤 WportHttpError／WportError）。
 */
async function createShell(opts: PersonalRequestOptions): Promise<string> {
	try {
		const { body } = await personalPost(opts, PERSONAL_RESUMES_BASE, {}, freshIdempotencyKey());
		return unwrapDataResponse<{ enc_id: string; name: string }>(body).enc_id;
	} catch (err) {
		if (err instanceof ServerClientHttpError) {
			const code = extractErrorCode(err.body);
			if (code === 'profile_incomplete') {
				throw new CliError(
					'Cannot create resume: your member profile is incomplete. Complete the required profile fields first, then retry (server: profile_incomplete).',
					ExitCode.ServerClientError
				);
			}
			if (code === 'resume_limit_reached') {
				throw new CliError(
					'Cannot create resume: you have reached your resume limit. Delete an existing resume first, then retry (server: resume_limit_reached).',
					ExitCode.ServerClientError
				);
			}
		}
		throw err;
	}
}

/** `name` 型別收窄為 string:呼叫端已用 `aggregate.name !== undefined` 排除 undefined,其餘型別不符交給 server 422 把關(update.ts 註解),這裡不重複加 runtime 檢查。 */
async function renameResume(opts: PersonalRequestOptions, encId: string, name: string): Promise<void> {
	await personalPut(opts, `${PERSONAL_RESUMES_BASE}/${encodeURIComponent(encId)}/name`, { name } satisfies UpdateResumeNameDto, freshIdempotencyKey());
}

/** work_experience item 的 is_current：聚合格式讀 boolean，寫入 DTO 收 0/1（讀寫不對稱，design doc §3.4）。 */
function toWorkExperienceWriteItem(item: Record<string, unknown>): Record<string, unknown> {
	const { is_current, ...rest } = item;
	return { ...rest, is_current: is_current ? 1 : 0 };
}

/**
 * 從 item 取出 R2 聚合讀取帶回的 `enc_id`（round-trip 用），回傳 [有效 enc_id | null, 剝除後的
 * body]。空白字串視同缺席；create 模式呼叫端直接丟棄 enc_id（新履歷不可能有既有 item id，
 * view 複製來的聚合直接 create 也能過），update 模式據此分派 PUT（更新既有）/ POST（新增）。
 */
function splitItemEncId(item: Record<string, unknown>): [string | null, Record<string, unknown>] {
	const { enc_id, ...rest } = item;
	const valid = typeof enc_id === 'string' && enc_id.trim() ? enc_id.trim() : null;
	return [valid, rest];
}

/**
 * 依 `SECTION_WRITE_PLAN` 分派單節寫入。`mode` 的作用：
 * - `single-post`：create 用 POST（新建），update 用 PUT（單物件節整份覆蓋，無 delete）
 * - `per-item-post` / `work-experience` 的 items：update 模式下 item 帶 `enc_id`（view round-trip）
 *   → 走 item 級 PUT 更新既有；無 `enc_id` → POST 新增（plan Task 14「帶 enc_id 走 PUT」）。
 *   create 模式一律剝除 enc_id 後 POST。**不做宣告式刪除**——不在輸入內的既有 item 不會被刪
 *  （portfolio_links 的 bulk-put 例外，該端點本身是宣告式）。
 * - `bulk-put`：item 的 `enc_id` 對映為後端 DTO 的 `encId`（update 保留既有連結；create 一律 null）
 *
 * 刻意不對 `value` 做結構性防呆（是否為陣列/物件）：呼叫端已篩掉 `undefined`，其餘型別錯誤
 * 讓 JS 原生地丟出（例如對非陣列 `for...of`），統一由 `attemptStep` 接住記成該節失敗 ——
 * 不重複 validate.ts 已有的型別檢查邏輯。
 */
async function writeSection(
	opts: PersonalRequestOptions,
	encId: string,
	key: AggregateSectionKey,
	value: unknown,
	mode: WriteMode
): Promise<void> {
	const plan = SECTION_WRITE_PLAN[key];
	const base = `${PERSONAL_RESUMES_BASE}/${encodeURIComponent(encId)}`;

	switch (plan.kind) {
		case 'per-item-post': {
			for (const item of value as Record<string, unknown>[]) {
				const [itemEncId, body] = splitItemEncId(item);
				if (mode === 'update' && itemEncId) {
					await personalPut(opts, `${base}${plan.path}/${encodeURIComponent(itemEncId)}`, body, freshIdempotencyKey());
				} else {
					await personalPost(opts, `${base}${plan.path}`, body, freshIdempotencyKey());
				}
			}
			return;
		}
		case 'work-experience': {
			const wrapper = value as { has_no_work_experience?: boolean; items: Record<string, unknown>[] };
			if (wrapper.has_no_work_experience) {
				await personalPost(opts, `${base}${plan.path}/no-work-experience`, {}, freshIdempotencyKey());
				return;
			}
			for (const item of wrapper.items) {
				const [itemEncId, rest] = splitItemEncId(item);
				const body = toWorkExperienceWriteItem(rest);
				if (mode === 'update' && itemEncId) {
					// 既有 web DTO 慣例：work-experience 的 PUT 以 body 內 `encId`（camelCase 例外）定位單筆
					await personalPut(opts, `${base}${plan.path}`, { encId: itemEncId, ...body }, freshIdempotencyKey());
				} else {
					await personalPost(opts, `${base}${plan.path}`, body, freshIdempotencyKey());
				}
			}
			return;
		}
		case 'single-post': {
			const body = key === 'autobiography' ? { autobiography: value } : (value as Record<string, unknown>);
			const write = mode === 'create' ? personalPost : personalPut;
			await write(opts, `${base}${plan.path}`, body, freshIdempotencyKey());
			return;
		}
		case 'bulk-put': {
			const items = value as Record<string, unknown>[];
			await personalPut(
				opts,
				`${base}${plan.path}`,
				{
					portfolio_links: items.map((item) => {
						const [itemEncId, rest] = splitItemEncId(item);
						return { encId: mode === 'update' ? itemEncId : null, ...rest };
					}),
				},
				freshIdempotencyKey()
			);
			return;
		}
	}
}

/** 單一步驟（rename 或某一節）成敗記錄成一筆 SectionReport；失敗不拋出，讓呼叫端繼續下一步（不回滾）。 */
async function attemptStep(section: string, run: () => Promise<void>): Promise<SectionReport> {
	try {
		await run();
		return { section, ok: true };
	} catch (err) {
		return { section, ok: false, error: describeError(err) };
	}
}

/**
 * `create --file` 編排核心：
 * ① 建殼（R3，失敗中止，見 createShell）
 * ② 聚合有 `name` → rename（D8：後端建殼不收 name，由 CLI 補；失敗記報告、不中止）
 * ③ 依 SECTIONS 固定順序，僅寫聚合檔內出現的節（單物件節用 POST）
 */
export async function createAggregate(opts: PersonalRequestOptions, aggregate: Record<string, unknown>): Promise<OrchestrationResult> {
	const encId = await createShell(opts);
	const reports: SectionReport[] = [];

	if (aggregate.name !== undefined) {
		reports.push(await attemptStep('name', () => renameResume(opts, encId, aggregate.name as string)));
	}

	for (const section of SECTIONS) {
		const value = aggregate[section.key];
		if (value === undefined) continue;
		reports.push(await attemptStep(section.key, () => writeSection(opts, encId, section.key, value, 'create')));
	}

	return { enc_id: encId, reports, allOk: reports.every((r) => r.ok) };
}

/**
 * `update <enc_id> --file [--section]` 編排核心。resume 已存在（無建殼步）。
 *
 * 頂層 `name`：整份模式（無 `onlySection`）下若聚合檔帶 `name` → 比照 create ②b 走 R4 rename
 * （記入 reports section='name'）——否則 view → 改名 → update 的改動會被靜默丟棄，且 CLI 將完全
 * 無法改名（final review low finding，2026-07-30 補）。`--section` 模式維持只寫指定節，`name`
 * 不是節、不在 `--section` 值域。
 *
 * `onlySection` 給定 → 只寫該節：未知節名或該節在聚合檔內缺席都是本地能擋的輸入錯誤，直接
 * CliError exit 2（不發請求）——未知節名訊息列出合法節名，mirror to-json-schema.ts 的
 * `buildAggregateJsonSchema` 慣例。未給 `onlySection` → 出現的節全寫，語意同 create③。
 *
 * 不做本地整份 `validateAggregate`：`--section` 模式下輸入檔可能只含單節內容，套用整份聚合
 * 驗證器（要求頂層 `name` 必填）會誤判；交給 server 422 把關，partial success 記入逐節報告。
 */
export async function updateAggregate(
	opts: PersonalRequestOptions,
	encId: string,
	aggregate: Record<string, unknown>,
	onlySection?: string
): Promise<OrchestrationResult> {
	const reports: SectionReport[] = [];

	if (onlySection !== undefined) {
		const section = getSection(onlySection);
		if (!section) {
			const allowed = SECTIONS.map((s) => s.key).join(', ');
			throw new CliError(`Unknown section "${onlySection}". Allowed: ${allowed}`, ExitCode.InvalidArgument);
		}
		const value = aggregate[onlySection];
		if (value === undefined) {
			throw new CliError(`Section "${onlySection}" has no data in the input file`, ExitCode.InvalidArgument);
		}
		reports.push(await attemptStep(section.key, () => writeSection(opts, encId, section.key, value, 'update')));
		return { enc_id: encId, reports, allOk: reports.every((r) => r.ok) };
	}

	if (aggregate.name !== undefined) {
		reports.push(await attemptStep('name', () => renameResume(opts, encId, aggregate.name as string)));
	}

	for (const section of SECTIONS) {
		const value = aggregate[section.key];
		if (value === undefined) continue;
		reports.push(await attemptStep(section.key, () => writeSection(opts, encId, section.key, value, 'update')));
	}

	return { enc_id: encId, reports, allOk: reports.every((r) => r.ok) };
}
